import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import pick from 'lodash/pick'
import head from 'lodash/head'
import { actions as LKModalActions } from '../../features/shared/LKModal'
import translate from 'loankeeper-common/helpers/language'

import { ThunkApi } from '../index.d'
import {
  ClientNoteAddEditAttributes,
  ClientNoteDeleteAttributes,
  ClientMonitoringAddEditAttributes,
  ClientMonitoringDeleteAttributes,
  ClientRiskLevelAddAttributes,
  ClientRiskLevelDeleteAttributes,
  ClientDelegateAddEditAttributes,
  ClientDelegateDeleteAttributes,
  ClientSearchAttributes,
  ClientAddParams,
  ClientCheckDublicateParams,
  ClientGetRequiredFiledsParams,
  ClinetRelationsAddAttributes,
  ClientRelationsDeleteAttributes,
  ClientAddToBlackListAttributes,
  ClientRemoveFromBlackListAttributes,
  ClientAddToWatchListAttributes,
  ClientRemoveFromWatchListAttributes,
  sendSMSParams,
  ClientAddRemovePermissionParams,
  ValidatePinCodeParams,
  ClinetAddIdDocParams,
  ClinetEditIdDocParams,
  ClientDeleteIdDocParams,
  ClientVerifySendSmsParams,
  ClientVerifySMSVerifyParams,
  SendNewPinSMSParams,
  ClientCheckReqFieldsParams,
  ClientTagAddParams,
  ClientDeleteTagParams,
  ClientViewPage,
} from '@lk/api'

export interface ClientAttributes {
  clientId: number
  clientHash: string
  additionalParams?: { [key: string]: any }
}

export const loadEditDetails = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_EDIT_DETAILS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getEdit(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientLoans = createAsyncThunk<Array<any>, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_LOANS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getLoans(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

interface RelatedParallelLoanPayload {
  id: number
  loans: Array<any>
}

export const loadClientParallelLoans = createAsyncThunk<RelatedParallelLoanPayload, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_PARALLEL_LOANS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getParallelLoans(clientId, clientHash)

    if (result.success) {
      const loans = result.data.map((item) => ({
        ...pick(item, [
          'DisburseDate',
          'StateID',
          'AgreementNo',
          'FullName',
          'OSBalance',
          'ISO',
          'Product',
          'Officer',
          'OVDDays',
          'TotalOVDDays',
        ]),
        Black: 0,
        CardNumber: '',
      }))

      return {
        id: clientId,
        loans,
      }
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientRelatedLoans = createAsyncThunk<RelatedParallelLoanPayload, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_RELATED_LOANS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getRelatedLoans(clientId, clientHash)

    if (result.success) {
      const loans = result.data.map((item) =>
        pick(item, [
          'Black',
          'DisburseDate',
          'StateID',
          'AgreementNo',
          'FullName',
          'OSBalance',
          'ISO',
          'Product',
          'Officer',
          'OVDDays',
          'TotalOVDDays',
          'CardNumber',
        ]),
      )

      return {
        id: clientId,
        loans,
      }
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientDelegates = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_DELEGATES',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getDelegates(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientNotes = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_NOTES',
  async ({ clientId, clientHash, additionalParams }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getActiveNotes(clientId, clientHash, additionalParams)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientLinkedFiles = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_LINKED_FILES',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getLinkedFiles(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientRating = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_RATING',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getRating(clientId, clientHash)

    if (result.success) {
      return head(result.data)
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientLoanStats = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_LOAN_STATS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getLoanStats(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientDetails = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_DETAILS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getBaseDetails(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientGeneralDetails = createAsyncThunk<any, ClientAttributes & { page: ClientViewPage }, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_DETAILS_GENERAL',
  async ({ clientId, clientHash, page, additionalParams }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getGeneralDetails({
      clientId,
      clientHash,
      page,
      additionalParams,
    })

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const printClient = createAsyncThunk<
  any,
  { ClientID: number; HashSum_ClientID_: string; page: ClientViewPage },
  ThunkApi
>('[CLIENT] LOAD_CLIENT_DETAILS_GENERAL', async ({ ClientID, HashSum_ClientID_, page }, { extra, rejectWithValue }) => {
  const result = await extra.api.client.getGeneralDetails({
    clientId: ClientID,
    clientHash: HashSum_ClientID_,
    page,
  })

  if (result.success) {
    return result.data
  }

  return rejectWithValue(result.error.message)
})

export const loadClientDynamicFields = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_DYNAMIC_FIELDS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getDynamicFields(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientCashDocs = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_CASH_DOCS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getCashDocs(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientIdDocs = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_CASH_DOCS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getIdDocs(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientDelegateCashDocs = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_DELEGATE_CASH_DOCS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getDelegateCashDocs(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const loadClientAccounts = createAsyncThunk<any, ClientAttributes, ThunkApi>(
  '[CLIENT] LOAD_CLIENT_ACCOUNTS',
  async ({ clientId, clientHash }, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getAccounts(clientId, clientHash)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const addEditClientNote = createAsyncThunk<any, ClientNoteAddEditAttributes, ThunkApi>(
  '[CLIENT] ADD_EDIT_CLIENT_NOTE',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addEditNote(attributes)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteClientNote = createAsyncThunk<any, ClientNoteDeleteAttributes, ThunkApi>(
  '[CLIENT] ADD_EDIT_CLIENT_NOTE',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.deleteNote(attributes)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const addEditClientMonitoring = createAsyncThunk<any, ClientMonitoringAddEditAttributes, ThunkApi>(
  '[CLIENT] ADD_EDIT_CLIENT_Monitoring',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addEditMonitoring(attributes)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteClientMonitoring = createAsyncThunk<any, ClientMonitoringDeleteAttributes, ThunkApi>(
  '[CLIENT] ADD_EDIT_CLIENT_Monitoring',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.deleteMonitoring(attributes)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const addClientRiskLevel = createAsyncThunk<any, ClientRiskLevelAddAttributes, ThunkApi>(
  '[CLIENT] ADD_EDIT_CLIENT_RiskLevel',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addRiskLevel(attributes)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteClientRiskLevel = createAsyncThunk<any, ClientRiskLevelDeleteAttributes, ThunkApi>(
  '[CLIENT] ADD_EDIT_CLIENT_RiskLevel',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.deleteRiskLevel(attributes)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const addEditClientDelegate = createAsyncThunk<any, ClientDelegateAddEditAttributes, ThunkApi>(
  '[CLIENT] ADD_EDIT_CLIENT_Delegate',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addEditDelegate(attributes)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const deleteClientDelegate = createAsyncThunk<any, ClientDelegateDeleteAttributes, ThunkApi>(
  '[CLIENT] ADD_EDIT_CLIENT_Delegate',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.deleteDelegate(attributes)

    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error.message)
  },
)

export const addClientRelation = createAsyncThunk<any, ClinetRelationsAddAttributes, ThunkApi>(
  '[CLIENT] ADD_CLIENT_RELATION',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addRelation(attributes)
    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error)
  },
)

export const deleteClientRelation = createAsyncThunk<any, ClientRelationsDeleteAttributes, ThunkApi>(
  '[CLIENT] DELETE_CLIENT_RELATION',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.deleteRelation(attributes)
    if (result.success) {
      return result.data
    }

    return rejectWithValue(result.error)
  },
)

export const addToBlacklist = createAsyncThunk<any, ClientAddToBlackListAttributes, ThunkApi>(
  '[CLIENT] ADD_TO_BLACKLIST',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addToBlacklist(attributes)
    if (result.success) {
      return result.data
    }
    return rejectWithValue(result.error)
  },
)

export const removeFromBlackList = createAsyncThunk<any, ClientRemoveFromBlackListAttributes, ThunkApi>(
  '[CLIENT] REMOVE_FROM_BLACKLIST',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.removeFromBlackList(attributes)
    if (result.success) {
      return result.data
    }
    return rejectWithValue(result.error)
  },
)

export const addToWatchlist = createAsyncThunk<any, ClientAddToWatchListAttributes, ThunkApi>(
  '[CLIENT] ADD_TO_WATCHLIST',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addToWatchlist(attributes)
    if (result.success) {
      return result.data
    }
    return rejectWithValue(result.error)
  },
)

export const removeFromWatchList = createAsyncThunk<any, ClientRemoveFromWatchListAttributes, ThunkApi>(
  '[CLIENT] REMOVE_FROM_WATCHLIST',
  async (attributes, { extra, rejectWithValue }) => {
    const result = await extra.api.client.removeFromWatchList(attributes)
    if (result.success) {
      return result.data
    }
    return rejectWithValue(result.error)
  },
)

export const clientSearch = createAsyncThunk<any, ClientSearchAttributes, ThunkApi>(
  '[CLIENT] CLIENT_SEARCH',
  async (attributes, { dispatch, extra, rejectWithValue }) => {
    const newAttributes = { ...attributes }
    const instanceName = newAttributes.instanceName

    if (instanceName) {
      delete newAttributes.instanceName
    }

    const result = await extra.api.client.clientSearch(newAttributes)

    if (result.success) {
      if (instanceName) {
        return {
          instanceName,
          data: result.data,
        }
      }
      return result.data
    } else {
      // dispatch(LKModalActions.openError(translate(`LKMESSAGE-${result.error.message}`)))
      throw new Error(result.error.message)
    }

    // return rejectWithValue(result.error.message)
    // throw new Error(result.error.message)
  },
)

export const clientSearchAgain = createAsyncThunk<any, string, ThunkApi>(
  '[CLIENT] CLIENT_SEARCH_AGAIN',
  async (instanceName, { dispatch, extra, rejectWithValue, getState }) => {
    const params = { ...getState().client.clientData.prevSearchData[instanceName] }

    if (params.instanceName) {
      delete params.instanceName
    }
    const result = await extra.api.client.clientSearch(params)

    if (result.success) {
      if (instanceName) {
        return {
          instanceName: instanceName,
          data: result.data,
        }
      }
      return result.data
    } else {
      // dispatch(LKModalActions.openError(translate(`LKMESSAGE-${result.error.message}`)))
      throw new Error(result.error.message)
    }

    // return rejectWithValue(result.error.message)
    // throw new Error(result.error.message)
  },
)

export const submitAddClientFormData = createAsyncThunk<
  any,
  Partial<ClientAddParams> & { instanceName: string },
  ThunkApi
>('[CLIENT] CLIENT_SUBMIT', async (attributes, { extra, rejectWithValue }) => {
  const result = await extra.api.client.clientAddFormData(attributes)

  if (result.success) {
    if (attributes.instanceName) {
      return {
        instanceName: attributes.instanceName,
        data: result.data,
      }
    }
    return result.data
  } else {
    throw new Error(result.error.message)
  }
})

export const clientCheckDublicate = createAsyncThunk<any, ClientCheckDublicateParams, ThunkApi>(
  '[CLIENT] CLIENT_CHECK_DUBLICATE',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.clientCheckDublicate(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const getClientReqFields = createAsyncThunk<any, ClientGetRequiredFiledsParams, ThunkApi>(
  '[CLIENT] CLIENT_GET_REQFIELDS',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getClientReqFields(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const checkReqFields = createAsyncThunk<any, ClientCheckReqFieldsParams, ThunkApi>(
  '[CLIENT] CLIENT_CHECK_REQFIELDS',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.checkClientReqFields(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const destroyReqFields = createAction('[CLIENT] DESTROY_CLIENT_CHECK_REQFIELDS')

export const getClientSelects = createAsyncThunk<any, void, ThunkApi>(
  '[CLIENT] CLIENT_GET_REQFIELDS',
  async (_, { extra, rejectWithValue }) => {
    const result = await extra.api.client.getSelects()
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const sendSMS = createAsyncThunk<any, sendSMSParams, ThunkApi>(
  '[CLIENT] CLIENT_SEND_SMS',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.sendSMS(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const sendNewPinSMS = createAsyncThunk<any, SendNewPinSMSParams, ThunkApi>(
  '[CLIENT] CLIENT_SEND_NEW_PIN_SMS',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.sendNewPinSMS(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const addPermission = createAsyncThunk<any, ClientAddRemovePermissionParams, ThunkApi>(
  '[CLIENT] CLIENT_ADD_PERMISSION',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addPermission(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const deletePermission = createAsyncThunk<any, ClientAddRemovePermissionParams, ThunkApi>(
  '[CLIENT] CLIENT_REMOVE_PERMISSION',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.deletePermission(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const validatePinCode = createAsyncThunk<any, ValidatePinCodeParams, ThunkApi>(
  '[CLIENT] CLIENT_VALIDATE_PIN_CODE',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.validatePinCode(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const clinetAddIdDoc = createAsyncThunk<any, ClinetAddIdDocParams, ThunkApi>(
  '[CLIENT] CLIENT_ADD_ID_DOC',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.clinetAddIdDoc(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const clinetEditIdDoc = createAsyncThunk<any, ClinetEditIdDocParams, ThunkApi>(
  '[CLIENT] CLIENT_EDIT_ID_DOC',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.clinetEditIdDoc(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const clinetDeleteIdDoc = createAsyncThunk<any, ClientDeleteIdDocParams, ThunkApi>(
  '[CLIENT] CLIENT_DELETE_ID_DOC',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.clientDeleteIdDoc(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const clientContactSendSMS = createAsyncThunk<any, ClientVerifySendSmsParams, ThunkApi>(
  '[CLIENT] CLIENT_CONTACT_SEND_SMS',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.clientContactSendSMS(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const clientContactVerifySMS = createAsyncThunk<any, ClientVerifySMSVerifyParams, ThunkApi>(
  '[CLIENT] CLIENT_CONTACT_VERIFY_SMS',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.clientContactVerifySMS(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const addTag = createAsyncThunk<any, ClientTagAddParams, ThunkApi>(
  '[CLIENT] ADD_TAG',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.addClientTag(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)

export const deleteTag = createAsyncThunk<any, ClientDeleteTagParams, ThunkApi>(
  '[CLIENT] DELETE_TAG',
  async (params, { extra, rejectWithValue }) => {
    const result = await extra.api.client.deleteClientTag(params)
    if (result.success) {
      return result.data
    } else {
      return rejectWithValue(result.error.message)
    }
  },
)
